// frontend/src/components/Layout/Header.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import LanguageSelector from '../Common/LanguageSelector';
import NotificationBell from '../Common/NotificationBell';
import './Header.css';

const Header = ({ sidebarCollapsed, breadcrumb = [] }) => {
  const { user, logout } = useAuth();
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className={`app-header ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="header-left">
        {/* Breadcrumb from live menu data */}
        <nav className="header-breadcrumb" aria-label="breadcrumb">
          {breadcrumb.length === 0 ? (
            <span className="breadcrumb-item active">{t('breadcrumb.home', 'Home')}</span>
          ) : (
            breadcrumb.map((item, i) => {
              const isLast = i === breadcrumb.length - 1;
              return (
                <React.Fragment key={item.id ?? i}>
                  {i > 0 && <span className="breadcrumb-separator">/</span>}
                  <span className={`breadcrumb-item ${isLast ? 'active' : ''}`}>
                    {item.icon && <span className="breadcrumb-icon">{item.icon}</span>}
                    {item.label}
                  </span>
                </React.Fragment>
              );
            })
          )}
        </nav>
      </div>

      <div className="header-right">
        {/* Language Selector */}
        <div className="header-language">
          <LanguageSelector compact={true} />
        </div>

        {/* Notifications */}
        <NotificationBell />

        {/* User Menu */}
        <div className="header-user" ref={userMenuRef}>
          <button className="user-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
            <div className="user-avatar">
              {getUserInitials(user?.full_name || user?.username)}
            </div>
            <div className="user-info">
              <span className="user-name">{user?.full_name || user?.username}</span>
              <span className="user-role">{user?.group_name || t('user.role', 'User')}</span>
            </div>
            <span className="user-arrow">▼</span>
          </button>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-avatar-large">
                  {getUserInitials(user?.full_name || user?.username)}
                </div>
                <div className="user-dropdown-info">
                  <p className="user-dropdown-name">{user?.full_name || user?.username}</p>
                  <p className="user-dropdown-email">{user?.email || t('user.no_email', 'No email')}</p>
                  <p className="user-dropdown-language">
                    {t('language', 'Language')}: <strong>{currentLanguage.toUpperCase()}</strong>
                  </p>
                </div>
              </div>
              <div className="user-dropdown-menu">
                <a href="/settings/profile" className="dropdown-item">
                  <span className="item-icon">👤</span>
                  {t('menu.profile', 'Profile')}
                </a>
                <a href="/settings/notifications" className="dropdown-item">
                  <span className="item-icon">🔔</span>
                  {t('menu.notifications', 'Notifications')}
                </a>
                <a href="/settings" className="dropdown-item">
                  <span className="item-icon">⚙️</span>
                  {t('menu.settings', 'Settings')}
                </a>
                <a href="/help" className="dropdown-item">
                  <span className="item-icon">❓</span>
                  {t('menu.help', 'Help & Support')}
                </a>
                <div className="dropdown-divider"></div>
                <button onClick={handleLogout} className="dropdown-item logout">
                  <span className="item-icon">🚪</span>
                  {t('menu.logout', 'Logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;